/**
 * The "New tickets from customers" card on the Settings page: who is emailed when a customer raises
 * a ticket, from the agent tray on their PC or by emailing the CRM's mailbox.
 *
 * Its own record and its own Save, like the lockout alert address: the tunables document is broadcast
 * to every agent, and an MSP's internal mailbox does not belong on a customer's machine.
 */
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import type { TicketAlertDto } from '../../contracts.gen.js';
import { localDateTime } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, field, GAP, h2, muted, relayout, stackOf, textBox, vstack } from '../../ui/layout.js';
import type { PageContext } from '../page.js';

export interface TicketAlertSection {
  card: Card;
  load(): Promise<void>;
}

export function ticketAlertSection(ctx: PageContext, canEdit: boolean): TicketAlertSection {
  const box = textBox({ placeholder: 'support@yourcompany.com', enabled: canEdit });
  const status = new StatusLine();
  const deliveryWarning = muted('', 'danger');
  const lastSent = muted('');
  const section = card();
  section.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('New tickets from customers'),
      muted(
        'Emailed when a customer raises a ticket - from the agent on their PC, or by emailing the mailbox above - one email per ticket. Everyone signed in to the CRM also sees it pop up. Leave the box blank to switch the emails off.'
      ),
      field('Send to', box),
      deliveryWarning,
      lastSent,
      ...(canEdit ? [stackOf(buttonRow(), button('Save', () => void save(), true))] : []),
      status.label
    )
  );

  function show(a: TicketAlertDto): void {
    box.text = a.notifyEmail ?? '';
    deliveryWarning.text =
      !a.deliverable && (a.notifyEmail ?? '').trim()
        ? 'Nothing can be sent to this address: the mail account above is not configured to send.'
        : '';
    deliveryWarning.visible = deliveryWarning.text.length > 0;
    lastSent.text = a.lastAlertUtc ? `Last email sent ${localDateTime(a.lastAlertUtc)}.` : '';
    lastSent.visible = lastSent.text.length > 0;
    relayout(section);
  }

  async function load(): Promise<void> {
    const a = await crm.getTicketAlerts(ctx.dispose.signal());
    if (!ctx.cancelled()) show(a);
  }

  async function save(): Promise<void> {
    status.busy('Saving…');
    try {
      const saved = await crm.saveTicketAlerts(box.text);
      if (ctx.cancelled()) return;
      show(saved);
      status.ok(
        (saved.notifyEmail ?? '').trim()
          ? 'Saved. An email goes out for the next ticket a customer raises.'
          : 'New-ticket emails are off.'
      );
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    }
  }

  return { card: section, load };
}
